import { useEffect } from 'react';
import { useRouter } from 'next/router';

/* Store */
import { useHomeHubStoreAnimationDirection, useHomeHubStoreActions } from '@scenes/HomeHub/store/client/homehub.store';

/* Query Selectors */
import { useHomeHubDataSelector } from '@scenes/HomeHub/store/queries/useHomeHub.selectors';

import { TAnimationFlow, TSurveyQuestion } from '@scenes/HomeHub/homehub.types';

/**
 * useQuestionDirectionFlow
 *
 * Given a users answers, we have to have the "previous" and "next" answers ready to go.
 * 1. Do they have a prev or next?
 * 2. Account for the exclusions
 * 3. Return a "finished/end" boolean
 * 4. Return a "beginning/start" boolean
 * 5. Return a 'direction' key
 *
 * Note:
 *   Continue Button: When we hit "continue" we are going "next"
 *   Back Button: When we hit "back" we are going "previous"
 *
 * Note: Since we do not allow mid-survey navigation, we don't have conditionally show the "back" button, and worry about that.
 *       Later, we can use the hooks values to conditionally show the "back" button, if they want to.
 */
const useAnimationDirectionFlow = ({ flowType }: { flowType?: TAnimationFlow['direction'] } = {}) => {
  /* Store */
  const { setAnimationDirection } = useHomeHubStoreActions();
  const animationDirection = useHomeHubStoreAnimationDirection();

  /* HOOKS */
  /* Get previous question query */
  /* Current Survey Flow the user is on */
  const { currentSurvey = {} } = useHomeHubDataSelector();
  const { query } = useRouter();

  /* CONSTANTS */
  const currentQueryQuestionParam = query.question;
  /* ------ Get pathable questions ------ */
  /* skip exclusion questions */
  const surveyQuestionsMinusExcludes =
    currentSurvey?.questions?.filter((question: TSurveyQuestion) => !currentSurvey.excepted_questions_ids?.includes(question.id)) ?? [];
  /* indexof current question */
  const positionOfCurrentQuestion = surveyQuestionsMinusExcludes.findIndex(
    (question: TSurveyQuestion) => question.data_type === currentQueryQuestionParam,
  );

  /* superstitials can be a question param or not */
  const adjustmentToIncrement = () => {
    if (positionOfCurrentQuestion === -1) {
      return currentQueryQuestionParam ? 0 : 1;
    }

    return positionOfCurrentQuestion + 1;
  };

  /* Next & Previous question params */
  const nextQuestionQuery = surveyQuestionsMinusExcludes[adjustmentToIncrement()]?.data_type;
  const previousQuestionQuery = surveyQuestionsMinusExcludes[positionOfCurrentQuestion - 1]?.data_type;

  /* Is our question query even valid for the current display */
  const isValidQuestionRoute = positionOfCurrentQuestion > -1;

  /* We are explitcly expressing a flowtype, set it in store */
  useEffect(() => {
    if (flowType) {
      setAnimationDirection({
        direction: flowType,
        query: query.question as string,
      });
    }
  }, [flowType, query.question, setAnimationDirection]);

  return {
    nextQuestionQuery,
    previousQuestionQuery,
    direction: flowType || animationDirection,
    isFinished: Boolean(!nextQuestionQuery),
    isBeginning: Boolean(!previousQuestionQuery),
    isValidQuestionRoute,
  };
};

export default useAnimationDirectionFlow;
